/**
 * List of interactive assessments (topic tests, class tests, practice exams)
 * for the selected subject/year in the student portal.
 */
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Timer, Trophy, Lock, Loader2, Play } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  topic_test: { label: "Topic Test", badge: "bg-sky-100 text-sky-800 border-sky-300" },
  class_test: { label: "Class Test", badge: "bg-amber-100 text-amber-800 border-amber-300" },
  practice_exam: { label: "Practice Exam", badge: "bg-purple-100 text-purple-800 border-purple-300" },
};

export const InteractiveExamList = ({ subject, yearNum, token, onOpen, refreshFlag, onState }) => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!subject || !yearNum || !token) { setExams([]); onState?.({ loading: false, count: 0 }); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      onState?.({ loading: true, count: 0 });
      try {
        const r = await fetch(`${API_URL}/api/interactive-exams/list/${encodeURIComponent(subject)}/${yearNum}?token=${token}`);
        const data = r.ok ? await r.json() : { exams: [] };
        if (!cancelled) {
          setExams(data.exams || []);
          onState?.({ loading: false, count: (data.exams || []).length });
        }
      } catch {
        if (!cancelled) { setExams([]); onState?.({ loading: false, count: 0 }); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [subject, yearNum, token, refreshFlag]);

  if (loading) {
    return (
      <div className="mb-6 flex justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      </div>
    );
  }
  if (!exams.length) return null;

  return (
    <div className="mb-6 border-2 border-emerald-300 rounded-xl overflow-hidden bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="interactive-exam-list">
      <div className="bg-gradient-to-r from-[#0F2A4A] to-[#0E7C7B] text-white px-4 py-3 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-yellow-300" />
        <span className="font-semibold uppercase tracking-wide text-sm">Interactive Assessments · Auto-marked</span>
        <Badge className="bg-yellow-400/20 text-yellow-100 border-yellow-300 ml-auto">{exams.length}</Badge>
      </div>
      <div className="p-4 space-y-3">
        {exams.map((ex) => {
          const kind = KIND_META[ex.kind] || KIND_META.practice_exam;
          const noAttempts = ex.attempts_remaining <= 0;
          return (
            <div key={ex.id} className="bg-white border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`exam-item-${ex.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge variant="outline" className={kind.badge}>{kind.label}</Badge>
                  {ex.lockdown && (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      <Lock className="h-3 w-3 mr-1" /> Lockdown
                    </Badge>
                  )}
                  {ex.strand_label && <span className="text-xs text-gray-500">{ex.strand_label}</span>}
                </div>
                <h4 className="font-semibold text-gray-900 truncate">{ex.title}</h4>
                <div className="flex items-center gap-4 text-xs text-gray-600 mt-1 flex-wrap">
                  <span><Timer className="h-3 w-3 inline mr-1" />{ex.time_limit_minutes} min</span>
                  <span>🎯 {ex.total_marks} marks</span>
                  <span>🎫 {ex.attempts_used}/{ex.max_attempts} attempts</span>
                  {ex.best_percentage !== null && ex.best_percentage !== undefined && (
                    <span className={ex.best_percentage >= 50 ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold"}>
                      <Trophy className="h-3 w-3 inline mr-1" />Best: {ex.best_percentage}%
                    </span>
                  )}
                </div>
              </div>
              <Button
                onClick={() => onOpen(ex.id)}
                disabled={false}
                className={noAttempts
                  ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  : "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-md"}
                data-testid={`open-exam-${ex.id}`}
              >
                <Play className="h-4 w-4 mr-2" />
                {noAttempts ? "View Results" : ex.attempts_used > 0 ? "Retry" : "Start"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InteractiveExamList;
